const ANALYTICS_CARD_PERMISSIONS = [
  {
    key: "dashboard",
    label: "Dashboard",
    description: "A macro view of key survey results & outcomes.",
  },
  {
    key: "factor",
    label: "Category Analytics",
    description: "Results for category & outcome questions.",
  },
  {
    key: "question",
    label: "Question Distributions",
    description: "Score distributions for category & outcome questions.",
  },
  {
    key: "question-analytics",
    label: "Question Ranking",
    description: "The raw score breakdown for each question in the survey",
  },
  {
    key: "heatmap",
    label: "Heatmapping",
    description: "Visualize response scores and patterns across your survey data.",
  },
  {
    key: "benchmarks",
    label: "Benchmarking",
    description: "Comparable results against known standards.",
  },
  {
    key: "feedback",
    label: "Comments",
    description: "Analysis on qualitative employee feedback.",
  },
  {
    key: "correlations",
    label: "Category Correlations",
    description: "Relationships between categories & outcomes.",
  },
  {
    key: "completion_timeline",
    label: "Completion Timeline",
    description: "Survey completion counts per hour.",
  },
  {
    key: "custom_analytics",
    label: "Custom Analytics",
    description: "Explore custom analysis tailored to your organization.",
  },
  {
    key: "thematic",
    label: "Thematic Analysis",
    description: "Quantifying prominent themes from your feedback.",
  },
  {
    key: "roi",
    label: "ROI Analysis",
    description: "Linking your survey results to organizational performance.",
  },
];

const ANALYTICS_CARD_KEY_SET = new Set(ANALYTICS_CARD_PERMISSIONS.map((card) => card.key));

const createAnalyticsPermissionState = (defaultValue = true) =>
  ANALYTICS_CARD_PERMISSIONS.reduce((acc, card) => {
    acc[card.key] = defaultValue;
    return acc;
  }, {});

const normalizeDetailKey = (detail) => {
  const candidates = [detail?.value_id, detail?.value, detail?.name_id, detail?.name];
  for (let i = 0; i < candidates.length; i += 1) {
    const candidate = candidates[i];
    if (typeof candidate === "string" && candidate.trim().length > 0) {
      const normalized = candidate.trim().toLowerCase();
      if (ANALYTICS_CARD_KEY_SET.has(normalized)) {
        return normalized;
      }
    }
  }
  return null;
};

const parseAnalyticsPermissionsFromDetails = (details) => {
  if (!Array.isArray(details)) return null;
  const parsedState = createAnalyticsPermissionState(false);
  let found = false;
  details.forEach((detail) => {
    const key = normalizeDetailKey(detail);
    if (key) {
      parsedState[key] = true;
      found = true;
    }
  });
  return found ? parsedState : null;
};

const analyticsPermissionsToDetails = (state) => {
  if (!state) return [];
  return ANALYTICS_CARD_PERMISSIONS.filter((card) => state[card.key]).map((card) => ({
    name: "analytics_card",
    name_id: "analytics_card",
    value_id: card.key,
    value: card.key,
    text: `Analytics access: ${card.label}`,
  }));
};

const isAnalyticsAuthDetail = (detail) => {
  if (!detail) return false;
  if (detail?.name === "analytics_card" || detail?.name_id === "analytics_card") {
    return true;
  }
  return Boolean(normalizeDetailKey(detail));
};

const hasAnyAnalyticsPermission = (state) =>
  Object.values(state || {}).some((value) => value === true);

const extractEmployeeAuthDetails = (permissionRecords, employeeId) => {
  if (!Array.isArray(permissionRecords) || !employeeId) return [];
  const record = permissionRecords.find(
    (item) => Number(item?.employee_id) === Number(employeeId)
  );
  const container = record?.updated || record?.created || record;
  return container?.auth_details || container?.details || [];
};

const filterAnalyticsOptionsForEmployee = (options, { employee, permissionRecords, employeeId }) => {
  if (!Array.isArray(options)) return [];
  const isAdmin = employee?.account_type === 5;
  if (isAdmin) {
    return options;
  }

  if (employee?.access_data_analytics !== 1) {
    return [];
  }

  const details = extractEmployeeAuthDetails(permissionRecords, employeeId);
  const parsedState = parseAnalyticsPermissionsFromDetails(details);
  if (!parsedState) {
    return options;
  }

  const allowedKeys = new Set(
    Object.keys(parsedState).filter((key) => parsedState[key])
  );

  return options.filter((option) => {
    if (!ANALYTICS_CARD_KEY_SET.has(option?.key)) {
      return true;
    }
    return allowedKeys.has(option.key);
  });
};

export {
  ANALYTICS_CARD_PERMISSIONS,
  ANALYTICS_CARD_KEY_SET,
  analyticsPermissionsToDetails,
  createAnalyticsPermissionState,
  extractEmployeeAuthDetails,
  filterAnalyticsOptionsForEmployee,
  hasAnyAnalyticsPermission,
  isAnalyticsAuthDetail,
  parseAnalyticsPermissionsFromDetails,
};
